import { useCallback, useEffect, useState } from 'react';

import {
  HOVER_AIM_DELAY_MS,
  HOVER_CLOSE_DELAY_MS,
  HOVER_INTENT_SENSITIVITY_PX,
  HOVER_OPEN_DELAY_MS,
  isAimingAt,
  recordAim,
  resetAim,
} from './hoverIntent';

type MenuFocusStrategy = 'first' | 'last';

type Point = { x: number; y: number };

type Session = {
  suppress: boolean;
  touch: boolean;
  pendingMenuFocus: MenuFocusStrategy | null;
  openTimer: number | null;
  closeTimer: number | null;
  hovered: boolean;
  rearm: boolean;
  sample: { x: number; y: number; previousX: number; previousY: number };
  hoverPanel: HTMLElement | null;
  dismiss: (() => void) | null;
  keyboardAnchor: Point | null;
};

const sessions = new WeakMap<HTMLElement, Session>();

let openTrigger: HTMLElement | null = null;

let hoveredTrigger: HTMLElement | null = null;

function sessionFor(trigger: HTMLElement) {
  const existing = sessions.get(trigger);
  if (existing) {
    return existing;
  }
  const created: Session = {
    suppress: false,
    touch: false,
    pendingMenuFocus: null,
    openTimer: null,
    closeTimer: null,
    hovered: false,
    rearm: false,
    sample: { x: 0, y: 0, previousX: 0, previousY: 0 },
    hoverPanel: null,
    dismiss: null,
    keyboardAnchor: null,
  };
  sessions.set(trigger, created);
  return created;
}

function otherOpenSession(trigger: HTMLElement | null) {
  return openTrigger && openTrigger !== trigger ? (sessions.get(openTrigger) ?? null) : null;
}

function isNear(point: Point, anchor: Point) {
  return Math.hypot(point.x - anchor.x, point.y - anchor.y) < HOVER_INTENT_SENSITIVITY_PX;
}

function isHeldByKeyboard(trigger: HTMLElement, point: Point) {
  const anchor = otherOpenSession(trigger)?.keyboardAnchor;
  return !!anchor && isNear(point, anchor);
}

function pointerAnchor() {
  const sample = hoveredTrigger ? sessions.get(hoveredTrigger)?.sample : undefined;
  return sample ? { x: sample.x, y: sample.y } : null;
}

function claimOpen(trigger: HTMLElement | null) {
  otherOpenSession(trigger)?.dismiss?.();
  openTrigger = trigger;
}

function cancelPopoverAnimations(hoverPanel: HTMLElement | null) {
  const popover = hoverPanel?.closest<HTMLElement>('[data-trigger]') ?? hoverPanel;
  if (!popover || !('getAnimations' in popover)) {
    return;
  }
  popover.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
}

function clearOpenTimer(session: Session) {
  if (session.openTimer !== null) {
    window.clearTimeout(session.openTimer);
  }
}

function clearCloseTimer(session: Session) {
  if (session.closeTimer !== null) {
    window.clearTimeout(session.closeTimer);
  }
}

const menuEntryKeys: Partial<Record<string, MenuFocusStrategy>> = {
  Enter: 'first',
  ' ': 'first',
  ArrowDown: 'first',
  ArrowUp: 'last',
};

function focusMenuItem(panel: HTMLElement | null, strategy: MenuFocusStrategy) {
  const items = panel?.querySelectorAll<HTMLElement>('[role="menu"] [role="menuitem"]');
  if (!items || items.length === 0) {
    return false;
  }
  items[strategy === 'first' ? 0 : items.length - 1].focus();
  return true;
}

type RailFlyoutOptions = {
  hoverPanel?: HTMLElement | null;
  getSubpanels?: (hoverPanel: HTMLElement) => HTMLElement[];
};

export function useRailFlyout(
  trigger: HTMLElement | null,
  panel: HTMLElement | null,
  { hoverPanel = panel, getSubpanels }: RailFlyoutOptions = {},
) {
  const [isOpen, setOpen] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  const show = useCallback(
    (byKeyboard = false) => {
      if (trigger) {
        const session = sessionFor(trigger);
        session.rearm = false;
        session.keyboardAnchor = byKeyboard ? pointerAnchor() : null;
      }
      claimOpen(trigger);
      setOpen(true);
    },
    [trigger],
  );

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open && !isTouch) {
        return;
      }
      const session = trigger ? sessionFor(trigger) : null;
      if (session) {
        clearOpenTimer(session);
        session.openTimer = null;
        clearCloseTimer(session);
        session.closeTimer = null;
      }
      if (open) {
        show(!session?.hovered);
        return;
      }
      setOpen(false);
    },
    [isTouch, trigger, show],
  );

  const close = useCallback(() => {
    if (!trigger) {
      setOpen(false);
      return;
    }
    const session = sessionFor(trigger);
    if (trigger.ownerDocument.activeElement !== trigger) {
      session.suppress = true;
    }
    clearOpenTimer(session);
    session.openTimer = null;
    clearCloseTimer(session);
    session.closeTimer = null;
    session.rearm = false;
    setOpen(false);
    trigger.focus();
  }, [trigger]);

  useEffect(() => {
    if (!trigger) {
      return;
    }
    return () => {
      if (openTrigger === trigger) {
        openTrigger = null;
      }
      if (hoveredTrigger === trigger) {
        hoveredTrigger = null;
      }
    };
  }, [trigger]);

  useEffect(() => {
    if (!trigger) {
      return;
    }
    const session = sessionFor(trigger);
    session.hoverPanel = hoverPanel;
    if (isOpen && openTrigger !== trigger) {
      claimOpen(trigger);
    } else if (!isOpen && openTrigger === trigger) {
      openTrigger = null;
    }

    const tryOpen = () => {
      session.openTimer = null;
      if (!session.hovered || isHeldByKeyboard(trigger, session.sample)) {
        return;
      }
      if (isAimingAt(otherOpenSession(trigger)?.hoverPanel ?? null)) {
        session.openTimer = window.setTimeout(tryOpen, HOVER_AIM_DELAY_MS);
        return;
      }
      show();
    };

    const checkIntent = () => {
      const { sample } = session;
      if (Math.hypot(sample.x - sample.previousX, sample.y - sample.previousY) < HOVER_INTENT_SENSITIVITY_PX) {
        tryOpen();
        return;
      }
      sample.previousX = sample.x;
      sample.previousY = sample.y;
      session.openTimer = window.setTimeout(checkIntent, HOVER_OPEN_DELAY_MS);
    };

    const scheduleOpen = (event: PointerEvent) => {
      session.pendingMenuFocus = null;
      clearCloseTimer(session);
      session.closeTimer = null;
      clearOpenTimer(session);
      session.openTimer = null;
      session.sample = { x: event.clientX, y: event.clientY, previousX: event.clientX, previousY: event.clientY };
      if (otherOpenSession(trigger)) {
        tryOpen();
        return;
      }
      session.openTimer = window.setTimeout(checkIntent, HOVER_OPEN_DELAY_MS);
    };

    const isInSubpanel = (node: EventTarget | null) =>
      node instanceof Node && !!hoverPanel && !!getSubpanels?.(hoverPanel).some((subpanel) => subpanel.contains(node));

    const isInHoverArea = (node: EventTarget | null) =>
      (node instanceof Node && !!hoverPanel?.contains(node)) || isInSubpanel(node);

    const hasFocusInside = () => isInHoverArea(trigger.ownerDocument.activeElement);

    const hide = (focusWasInside: boolean) => {
      setOpen(false);
      if (focusWasInside) {
        session.suppress = true;
        trigger.focus();
      }
    };

    const scheduleClose = () => {
      clearOpenTimer(session);
      session.openTimer = null;
      clearCloseTimer(session);
      const closeUnlessAiming = () => {
        if (isAimingAt(session.hoverPanel)) {
          session.closeTimer = window.setTimeout(closeUnlessAiming, HOVER_AIM_DELAY_MS);
          return;
        }
        session.closeTimer = null;
        hide(hasFocusInside());
      };
      session.closeTimer = window.setTimeout(closeUnlessAiming, HOVER_CLOSE_DELAY_MS);
    };

    const onPointerDown = (event: PointerEvent) => {
      session.touch = event.pointerType === 'touch';
      setIsTouch(session.touch);
    };

    const onClick = (event: MouseEvent) => {
      if (session.touch) {
        event.preventDefault();
      }
    };

    const onPointerEnter = (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        session.touch = true;
        setIsTouch(true);
        return;
      }
      session.hovered = true;
      hoveredTrigger = trigger;
      scheduleOpen(event);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        return;
      }
      if (
        !isOpen &&
        session.rearm &&
        !otherOpenSession(trigger) &&
        (event.clientX !== session.sample.x || event.clientY !== session.sample.y)
      ) {
        scheduleOpen(event);
        return;
      }
      session.sample.x = event.clientX;
      session.sample.y = event.clientY;
    };

    const onPointerLeave = (event: PointerEvent) => {
      if (event.pointerType === 'touch') {
        return;
      }
      if (event.currentTarget === trigger && hoveredTrigger === trigger) {
        hoveredTrigger = null;
      }
      session.hovered = false;
      session.rearm = false;
      scheduleClose();
    };

    const onFocus = () => {
      if (session.touch || session.suppress) {
        session.suppress = false;
        return;
      }
      clearOpenTimer(session);
      session.openTimer = null;
      clearCloseTimer(session);
      session.closeTimer = null;
      show(!session.hovered);
    };

    const onBlur = (event: FocusEvent) => {
      const next = event.relatedTarget;
      if (next instanceof Node && panel?.contains(next)) {
        return;
      }
      scheduleClose();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      event.stopPropagation();
      close();
    };

    const onTriggerKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onKeyDown(event);
        return;
      }
      if (event.key === 'Enter' && trigger instanceof HTMLAnchorElement) {
        session.touch = false;
        event.stopPropagation();
        return;
      }
      const strategy = menuEntryKeys[event.key];
      if (!strategy || event.ctrlKey || event.metaKey) {
        return;
      }
      if (focusMenuItem(panel, strategy)) {
        event.preventDefault();
        return;
      }
      if (isOpen) {
        return;
      }
      event.preventDefault();
      clearOpenTimer(session);
      session.openTimer = null;
      clearCloseTimer(session);
      session.closeTimer = null;
      session.pendingMenuFocus = strategy;
      show(true);
    };

    const onPanelEnter = () => {
      clearOpenTimer(session);
      session.openTimer = null;
      clearCloseTimer(session);
      session.closeTimer = null;
    };

    session.dismiss = () => {
      const focusWasInside = hasFocusInside();
      clearOpenTimer(session);
      session.openTimer = null;
      clearCloseTimer(session);
      session.closeTimer = null;
      session.rearm = session.hovered;
      cancelPopoverAnimations(hoverPanel);
      hide(focusWasInside);
    };

    const onHoverPanelLeave = (event: PointerEvent) => {
      if (isInSubpanel(event.relatedTarget)) {
        return;
      }
      onPointerLeave(event);
    };

    const onDocumentPointerOver = (event: PointerEvent) => {
      if (isInSubpanel(event.target)) {
        onPanelEnter();
      }
    };

    const onDocumentPointerOut = (event: PointerEvent) => {
      if (isInSubpanel(event.target) && !isInHoverArea(event.relatedTarget)) {
        onPointerLeave(event);
      }
    };

    const onDocumentPointerMove = (event: PointerEvent) => {
      recordAim(event);
      const anchor = session.keyboardAnchor;
      if (anchor && !isNear({ x: event.clientX, y: event.clientY }, anchor)) {
        session.keyboardAnchor = null;
      }
    };

    const subpanelDocument = getSubpanels ? hoverPanel?.ownerDocument : undefined;
    const aimDocument = isOpen ? trigger.ownerDocument : undefined;
    if (aimDocument) {
      resetAim();
    }

    trigger.addEventListener('pointerdown', onPointerDown);
    trigger.addEventListener('click', onClick);
    trigger.addEventListener('pointerenter', onPointerEnter);
    trigger.addEventListener('pointermove', onPointerMove);
    trigger.addEventListener('pointerleave', onPointerLeave);
    trigger.addEventListener('focus', onFocus);
    trigger.addEventListener('blur', onBlur);
    trigger.addEventListener('keydown', onTriggerKeyDown);
    hoverPanel?.addEventListener('pointerenter', onPanelEnter);
    hoverPanel?.addEventListener('pointerleave', onHoverPanelLeave);
    subpanelDocument?.addEventListener('pointerover', onDocumentPointerOver);
    subpanelDocument?.addEventListener('pointerout', onDocumentPointerOut);
    aimDocument?.addEventListener('pointermove', onDocumentPointerMove);
    panel?.addEventListener('keydown', onKeyDown);

    if (!isOpen) {
      session.pendingMenuFocus = null;
    }
    let focusFrame: number | null = null;
    const pendingMenuFocus = session.pendingMenuFocus;
    if (panel && pendingMenuFocus) {
      session.pendingMenuFocus = null;
      focusFrame = window.requestAnimationFrame(() => {
        focusFrame = null;
        focusMenuItem(panel, pendingMenuFocus);
      });
    }

    return () => {
      trigger.removeEventListener('pointerdown', onPointerDown);
      trigger.removeEventListener('click', onClick);
      trigger.removeEventListener('pointerenter', onPointerEnter);
      trigger.removeEventListener('pointermove', onPointerMove);
      trigger.removeEventListener('pointerleave', onPointerLeave);
      trigger.removeEventListener('focus', onFocus);
      trigger.removeEventListener('blur', onBlur);
      trigger.removeEventListener('keydown', onTriggerKeyDown);
      hoverPanel?.removeEventListener('pointerenter', onPanelEnter);
      hoverPanel?.removeEventListener('pointerleave', onHoverPanelLeave);
      subpanelDocument?.removeEventListener('pointerover', onDocumentPointerOver);
      subpanelDocument?.removeEventListener('pointerout', onDocumentPointerOut);
      aimDocument?.removeEventListener('pointermove', onDocumentPointerMove);
      panel?.removeEventListener('keydown', onKeyDown);
      if (focusFrame !== null) {
        window.cancelAnimationFrame(focusFrame);
      }
    };
  }, [trigger, panel, hoverPanel, getSubpanels, isOpen, close, show]);

  useEffect(() => {
    if (!trigger) {
      return;
    }
    const session = sessionFor(trigger);
    return () => {
      clearOpenTimer(session);
      session.openTimer = null;
      clearCloseTimer(session);
      session.closeTimer = null;
    };
  }, [trigger]);

  return { isOpen, isTouch, onOpenChange, close };
}
